import React, { createRef, Component } from 'react';
import { buildPath } from '../utils/route_utils.js';
import { observeRise } from '../utils/reveal.js';

import '../css/CollabProcessSection.css';

/**
 * CollabProcessSection — "How to Collaborate?" as a full-bleed photo band.
 * The collaboration photo sits behind the whole band (set inline with
 * PUBLIC_URL, like every other photo background in this codebase) under
 * the neutral scrim defined in CSS. Title, two tabs, a four-column step
 * strip and a ghost CTA; no eyebrow, no cards.
 *
 * Props: lang ('zh' | 'en' | 'ja'), navigate(route).
 */

const BACKDROP = process.env.PUBLIC_URL + '/image/collaboration_main_1800.jpg';

class CollabProcessSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lang: this.props.lang,
      activeTab: "standard",
      sectionTitle: {
        "zh": "合作流程",
        "en": "How to Collaborate?",
        "ja": "協業プロセス"
      },
      tabsLabel: {
        "zh": "合作模式",
        "en": "Collaboration model",
        "ja": "協業モデル"
      },
      tabs: {
        standard: { "zh": "標準合作", "en": "Standard Collaboration", "ja": "通常の協業" },
        launch: { "zh": "首發客戶", "en": "Launch Customer", "ja": "ローンチカスタマー" }
      },
      standardSteps: [
        {
          num: "01",
          title: { "zh": "選擇產品", "en": "Select a product", "ja": "製品選定" },
          desc: { "zh": "從 Nexuni 產品型錄中挑選適合的解決方案", "en": "Pick products from Nexuni's catalog", "ja": "Nexuni のカタログから製品を選択" }
        },
        {
          num: "02",
          title: { "zh": "諮詢討論", "en": "Consult", "ja": "コンサルティング" },
          desc: { "zh": "與我們的團隊安排會議討論需求", "en": "Schedule a meeting with our team to discuss your needs", "ja": "チームとのミーティングでニーズを相談" }
        },
        {
          num: "03",
          title: { "zh": "取得報價", "en": "Quote", "ja": "見積もり" },
          desc: { "zh": "獲得量身打造的報價方案", "en": "Receive a tailored quotation", "ja": "カスタマイズされた見積もりを受け取る" }
        },
        {
          num: "04",
          title: { "zh": "部署上線", "en": "Deploy", "ja": "導入展開" },
          desc: { "zh": "系統安裝、教育訓練與技術支援", "en": "System installation, training and technical support", "ja": "システム設置・トレーニング・技術サポート" }
        }
      ],
      launchSteps: [
        {
          num: "01",
          title: { "zh": "簽署 MOU & 前線部署", "en": "MOU & Forward Deploy", "ja": "MOU 締結 & 現場派遣" },
          desc: { "zh": "簽署合作備忘錄，派駐工程師至客戶現場了解人力挑戰", "en": "Sign an MOU and send an engineer to your site to understand your labour challenges", "ja": "MOU を締結し、エンジニアを現場に派遣して人手の課題を把握" }
        },
        {
          num: "02",
          title: { "zh": "提案與最低訂購量報價", "en": "Proposal & MOQ Quote", "ja": "提案・最小発注数見積" },
          desc: { "zh": "根據現場調查結果提供解決方案提案與最低訂購量報價", "en": "Propose a solution and quote with minimum order quantity based on findings", "ja": "調査結果に基づくソリューション提案と最小発注数での見積もり" }
        },
        {
          num: "03",
          title: { "zh": "設計與開發", "en": "Design & Develop", "ja": "設計・開発" },
          desc: { "zh": "針對您的需求設計並開發專屬的機器人與自動化產品", "en": "Design and develop robots and automation products tailored to your needs", "ja": "ニーズに合わせたロボット・自動化製品を設計・開発" }
        },
        {
          num: "04",
          title: { "zh": "部署與持續進化", "en": "Deploy & Evolve", "ja": "導入・継続進化" },
          desc: { "zh": "部署系統，持續監控、維護與 AI 學習適應新場景", "en": "Deploy, then keep monitoring and maintaining while the AI learns to adapt to new scenarios", "ja": "導入後も監視・保守を続け、AI が学習して新しい現場に適応" }
        }
      ],
      ctaText: {
        "zh": "開始合作",
        "en": "Start collaborating",
        "ja": "協業を始める"
      }
    };

    this.rootRef = createRef();
    this.disposeRise = null;
    this.navigate = this.navigate.bind(this);
  }

  componentDidMount() {
    this.disposeRise = observeRise(this.rootRef.current);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.lang !== this.state.lang) {
      this.setState({ lang: nextProps.lang });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Switching tabs remounts the step columns (keyed on activeTab), so the
    // fresh .rise nodes need observing again.
    if (prevState.activeTab !== this.state.activeTab) {
      if (this.disposeRise) this.disposeRise();
      this.disposeRise = observeRise(this.rootRef.current);
    }
  }

  componentWillUnmount() {
    if (this.disposeRise) this.disposeRise();
  }

  navigate(route) {
    if (this.props.navigate) {
      this.props.navigate(route);
    } else {
      window.location.href = buildPath(this.state.lang, route);
    }
  }

  render() {
    const lang = this.state.lang;
    const { activeTab } = this.state;
    const steps = activeTab === "standard" ? this.state.standardSteps : this.state.launchSteps;
    const ctaRoute = '/about/colab_flow';

    return (
      <section className="collab-process-section" ref={this.rootRef} aria-labelledby="collab-process-title">
        <div className="collab-process-bg" aria-hidden="true"
             style={{ backgroundImage: 'url(' + BACKDROP + ')' }}></div>

        <div className="collab-process-head rise">
          <h2 className="collab-process-title h-display" id="collab-process-title">{this.state.sectionTitle[lang]}</h2>
        </div>

        <div className="collab-tabs rise" role="tablist" aria-label={this.state.tabsLabel[lang]}>
          <button
            type="button"
            role="tab"
            id="collab-tab-standard"
            aria-selected={activeTab === "standard"}
            aria-controls="collab-panel"
            className={"collab-tab" + (activeTab === "standard" ? " collab-tab-active collab-tab-standard" : "")}
            onClick={() => this.setState({ activeTab: "standard" })}
          >
            {this.state.tabs.standard[lang]}
          </button>
          <button
            type="button"
            role="tab"
            id="collab-tab-launch"
            aria-selected={activeTab === "launch"}
            aria-controls="collab-panel"
            className={"collab-tab" + (activeTab === "launch" ? " collab-tab-active collab-tab-launch" : "")}
            onClick={() => this.setState({ activeTab: "launch" })}
          >
            {this.state.tabs.launch[lang]}
          </button>
        </div>

        <div className={"collab-stepper collab-stepper-" + activeTab} key={activeTab}
             id="collab-panel" role="tabpanel" aria-labelledby={"collab-tab-" + activeTab}>
          {steps.map((step, index) => (
            <div className="collab-step rise" key={activeTab + index}
                 style={{ transitionDelay: (index * 60) + "ms" }}>
              <div className="collab-step-num" aria-hidden="true">{step.num}</div>
              <div className="collab-step-content">
                <h3 className="collab-step-title">{step.title[lang]}</h3>
                <p className="collab-step-desc">{step.desc[lang]}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="collab-process-cta-wrap rise">
          <a className="collab-process-cta btn-ghost" href={buildPath(lang, ctaRoute)}
             onClick={(e) => { e.preventDefault(); this.navigate(ctaRoute); }}>
            {this.state.ctaText[lang]}
            <span className="arrow" aria-hidden="true">&rarr;</span>
          </a>
        </div>
      </section>
    );
  }
}

export default CollabProcessSection;
